import {ACTIVE_TRANSPORTER_PARAM} from 'common/active-transporter';
import React, { useContext } from 'react';
import { Typography } from 'antd';
import { schedulingCustomerOnly } from 'common/scheduling-access';
import { OrgDataContext } from 'context/orgData';
import useUserGroups from 'hooks/useUserGroups';
import { PersonDeletionImpact } from './operation';

type AssignedFlight = NonNullable<PersonDeletionImpact['passengers'][number]['currentFlight']>;

/** Links to the correct flight editor only when its context and scheduling access are available. */
const FlightLink: React.FC<{flight: AssignedFlight; personID: string}> = ({flight, personID}) => {
    const [groups] = useUserGroups();
    const orgData = useContext(OrgDataContext);
    const customerID = orgData?.getOrgIDByType('customer');
    const scheduled = flight.state === 'SCHEDULED';
    const canSchedule = groups.some(group => [
        'flytsuite.schedule.flight', 'flytsuite.schedule.passenger'
    ].includes(group));

    let disabledReason: string;
    if (customerID && flight.customerID !== customerID) {
        disabledReason = flight.customerID
            ? 'This flight belongs to another customer.'
            : 'The flight customer is unavailable.';
    } else if (!flight.state) {
        disabledReason = 'Flight details are unavailable.';
    } else if (scheduled && schedulingCustomerOnly && !customerID) {
        disabledReason = 'Scheduling is only available to customer accounts.';
    } else if (scheduled && !canSchedule) {
        disabledReason = 'Scheduling permission is required to open this flight.';
    }

    const label = scheduled ? 'Fix in scheduling' : 'Fix in flights';
    if (disabledReason) {
        return <Typography.Text disabled aria-disabled="true" title={disabledReason}>
            {label}
        </Typography.Text>;
    }

    const path = scheduled ? '/app/scheduling/flight' : '/app/flights';
    const query = new URLSearchParams({guidance: 'remove-passenger', highlightPersonID: personID});
    if (flight.transporterID) query.set(ACTIVE_TRANSPORTER_PARAM, flight.transporterID);
    return <a href={`${path}/${encodeURIComponent(flight.id)}?${query}`} target="_blank" rel="noopener noreferrer">
        {label}
    </a>;
};

export default FlightLink;
